import React, { useEffect, useState } from "react";
import useSWR, { mutate } from "swr";
import { config } from "~/core";
import Table from "../components/tables/Table";
import { apiProvider } from "../api/core/provider";
import { toast } from "react-toastify";
import FSButton from "../components/Buttons/FSButton";
import { STATE_NAMES, getStateName } from "../utilities/states";

export default function InspectorCountyMapping() {
  const BASE_URL = "/api/" + config.api.version;

  const canEdit = config.countyMappingCanEdit === true;
  const canViewReport = config.countyMappingCanViewReport === true;

  // Tab state
  const [activeTab, setActiveTab] = useState(canEdit ? "inspector" : "report"); // "inspector", "county", or "report"

  // Inspector-based view state
  const [inspectors, setInspectors] = useState(null);
  const [selectedInspector, setSelectedInspector] = useState(null);

  // County-based view state
  const [allCounties, setAllCounties] = useState([]);
  const [selectedCountyState, setSelectedCountyState] = useState(""); // State selection for county view
  const [countyStateCounties, setCountyStateCounties] = useState([]); // Counties for selected state in county view
  const [selectedCounty, setSelectedCounty] = useState(null);
  const [assignedInspectors, setAssignedInspectors] = useState([]);
  const [availableInspectors, setAvailableInspectors] = useState([]);
  // Shared state for both views
  const [states, setStates] = useState([]);
  const [stateCounties, setStateCounties] = useState([]);
  const [assignedCounties, setAssignedCounties] = useState([]);
  const [availableCounties, setAvailableCounties] = useState([]);
  // Weight editing state (shared between views)
  const [editingWeight, setEditingWeight] = useState(null);
  const [newWeight, setNewWeight] = useState(1);
  // UI state (shared between views)
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedState, setSelectedState] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingCounties, setLoadingCounties] = useState(false);
  const [batchLoading, setBatchLoading] = useState(false);
  const [selectedCounties, setSelectedCounties] = useState(new Set());
  const [selectedInspectors, setSelectedInspectors] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [selectAllInspectors, setSelectAllInspectors] = useState(false);
  // Report view state
  const [reportRows, setReportRows] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [expandedStates, setExpandedStates] = useState(new Set());

  // Mutations return 204 NoContent on success; a server error returns a JSON
  // envelope. axios.validateStatus is set to always resolve, so we must inspect
  // the response ourselves before treating a mutation as successful.
  const mutationOk = (res) => {
    if (res && res.data && typeof res.data === "object" && "status" in res.data) {
      return res.data.status === 200;
    }
    return !!res && res.status >= 200 && res.status < 300;
  };

  // Fetch inspectors
  useEffect(() => {
    if (!canEdit) {return;}
    let cancelled = false;
    
    apiProvider.get("inspectors").then((response) => {
      if (!cancelled) {
        setInspectors(response.data.result || []);
      }
    }).catch((error) => {
      if (!cancelled) {
        toast.error("Error loading inspectors");
        console.error("Error loading inspectors:", error);
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch all states and counties
  useEffect(() => {
    if (!canEdit) {return;}
    let cancelled = false;
    
    // Get all counties for both states list and county selector
    apiProvider.get("counties").then((response) => {
      if (!cancelled) {
        const counties = response.data.result || response.data || [];
        setAllCounties(counties);
        const uniqueStates = [...new Set(counties.map(county => county.state))].sort();
        setStates(uniqueStates);
      }
    }).catch((error) => {
      if (!cancelled) {
        toast.error("Error loading counties");
        console.error("Error loading counties:", error);
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch assigned counties when inspector is selected
  useEffect(() => {
    let cancelled = false;
    
    if (selectedInspector) {
      setLoading(true);
      apiProvider.get(`counties/inspector/${selectedInspector}`).then((response) => {
        if (!cancelled) {
          setAssignedCounties(response.data.result || response.data || []);
          setLoading(false);
          // Clear selections when switching inspectors
          setSelectedCounties(new Set());
          setSelectAll(false);
        }
      }).catch((error) => {
        if (!cancelled) {
          toast.error("Error loading assigned counties");
          console.error("Error loading assigned counties:", error);
          setLoading(false);
        }
      });
    } else {
      if (!cancelled) {
        setAssignedCounties([]);
        setSelectedCounties(new Set());
        setSelectAll(false);
      }
    }

    return () => {
      cancelled = true;
    };
  }, [selectedInspector]);

  // Fetch counties for selected state
  useEffect(() => {
    if (selectedState) {
      setLoadingCounties(true);
      setSearchTerm(""); // Reset search when state changes
      apiProvider.get(`counties/state/${selectedState}`).then((response) => {
        const counties = response.data.result || response.data || [];
        setStateCounties(counties);
        setLoadingCounties(false);
      }).catch((error) => {
        toast.error(`Error loading counties for ${selectedState}`);
        console.error("Error loading counties:", error);
        setLoadingCounties(false);
      });
    } else {
      setStateCounties([]);
      setSearchTerm(""); // Reset search when no state selected
    }
  }, [selectedState]);

  // Load inspectors for the selected county (county-based view): one GET for the
  // county's assigned inspectors plus one for the full inspector list, replacing
  // the old N+1 fan-out that silently dropped inspectors when a request failed.
  const loadCountyInspectors = async (countyId, isCancelled = () => false) => {
    try {
      const [inspRes, assignedRes] = await Promise.all([
        apiProvider.get("inspectors"),
        apiProvider.get(`counties/${countyId}/inspectors`),
      ]);
      if (isCancelled()) {return;}
      const allInspectors = inspRes.data.result || [];
      const assignedRows = assignedRes.data.result || [];
      const assigned = assignedRows.map(r => ({
        id: `${r.inspectorId}-${countyId}`,
        inspector: { id: r.inspectorId, name: r.inspector },
        weight: r.weight,
      }));
      const assignedInspectorIds = assigned.map(a => a.inspector.id);
      const available = allInspectors.filter(inspector => !assignedInspectorIds.includes(inspector.id));
      setAssignedInspectors(assigned);
      setAvailableInspectors(available);
      setSelectedInspectors(new Set());
      setSelectAllInspectors(false);
    } catch (error) {
      if (isCancelled()) {return;}
      toast.error("Error loading inspector assignments");
      console.error("Error loading inspector assignments:", error);
    } finally {
      if (!isCancelled()) {setLoading(false);}
    }
  };

  // Fetch assigned inspectors when county is selected (county-based view)
  useEffect(() => {
    let cancelled = false;
    
    if (selectedCounty && activeTab === "county") {
      setLoading(true);
      loadCountyInspectors(selectedCounty.id, () => cancelled);
    } else if (activeTab === "county") {
      if (!cancelled) {
        setAssignedInspectors([]);
        setAvailableInspectors([]);
        setSelectedInspectors(new Set());
        setSelectAllInspectors(false);
      }
    }

    return () => {
      cancelled = true;
    };
  }, [selectedCounty, activeTab]);

  // Fetch counties for selected state in county-based view
  useEffect(() => {
    if (selectedCountyState && activeTab === "county") {
      setLoadingCounties(true);
      apiProvider.get(`counties/state/${selectedCountyState}`).then((response) => {
        const counties = response.data.result || response.data || [];
        setCountyStateCounties(counties);
        setLoadingCounties(false);
        // Reset selected county when state changes
        setSelectedCounty(null);
      }).catch((error) => {
        toast.error(`Error loading counties for ${getStateName(selectedCountyState)}`);
        console.error("Error loading counties:", error);
        setLoadingCounties(false);
      });
    } else if (activeTab === "county") {
      setCountyStateCounties([]);
      setSelectedCounty(null);
    }
  }, [selectedCountyState, activeTab]);

  // Fetch county assignment report every time the report tab is activated so
  // edits made on the other tabs are reflected.
  useEffect(() => {
    if (activeTab !== "report") {return;}
    let cancelled = false;
    setReportLoading(true);
    apiProvider.get("counties/assignments/report").then((response) => {
      if (!cancelled) {
        setReportRows(response.data.result || []);
        setReportLoading(false);
      }
    }).catch((error) => {
      if (!cancelled) {
        toast.error("Error loading report");
        console.error("Error loading report:", error);
        setReportLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, [activeTab]);

  // Calculate available counties
  useEffect(() => {
    if (Array.isArray(stateCounties) && stateCounties.length > 0 && Array.isArray(assignedCounties)) {
      const assignedCountyIds = assignedCounties.map(ac => ac.county.id);
      const available = stateCounties.filter(county => !assignedCountyIds.includes(county.id));
      setAvailableCounties(available);
    } else if (Array.isArray(stateCounties) && stateCounties.length > 0) {
      // If we have state counties but no assigned counties array yet, all counties are available
      setAvailableCounties(stateCounties);
    } else {
      setAvailableCounties([]);
    }
  }, [stateCounties, assignedCounties]);

  // Filter available counties based on search
  const filteredAvailableCounties = availableCounties.filter(county => {
    const matchesSearch = county.county.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  // Count of the inspector's assigned counties in the selected state (for the
  // state-level clear control).
  const stateAssignedCount = selectedState && Array.isArray(assignedCounties)
    ? assignedCounties.filter(ac => ac.county.state === selectedState).length
    : 0;

  // Columns for assigned counties table
  const assignedCols = [
    { 
      Header: () => 
        <input
          type="checkbox"
          checked={selectAll}
          onChange={(e) => handleSelectAll(e.target.checked)}
          className="tw-w-4 tw-h-4 tw-text-blue-600 tw-bg-gray-100 tw-border-gray-300 tw-rounded focus:tw-ring-blue-500"
        />
      ,
      accessor: "id",
      Cell: ({ row }) => 
        <input
          type="checkbox"
          checked={selectedCounties.has(row.original.id)}
          onChange={(e) => handleSelectCounty(row.original.id, e.target.checked)}
          className="tw-w-4 tw-h-4 tw-text-blue-600 tw-bg-gray-100 tw-border-gray-300 tw-rounded focus:tw-ring-blue-500"
        />
      ,
      width: 50
    },
    { Header: "County", accessor: "county.county" },
    { 
      Header: "State", 
      accessor: "county.state",
      Cell: ({ value }) => getStateName(value)
    },
    { 
      Header: "Weight", 
      accessor: "weight",
      Cell: ({ row }) => 
        <div className="tw-flex tw-items-center tw-gap-2">
          {editingWeight === row.original.id ? 
            <div className="tw-flex tw-items-center tw-gap-2">
              <input
                type="number"
                min="1"
                value={newWeight}
                onChange={(e) => setNewWeight(parseInt(e.target.value) || 1)}
                className="tw-w-16 tw-px-2 tw-py-1 tw-border tw-rounded"
              />
              <FSButton
                onClick={() => saveWeight(row.original)}
                color="green"
                size="xsmall"
              >
                Save
              </FSButton>
              <FSButton
                onClick={() => setEditingWeight(null)}
                color="secondary"
                size="xsmall"
              >
                Cancel
              </FSButton>
            </div>
            : 
            <div className="tw-flex tw-items-center tw-gap-2">
              <span>{row.original.weight}</span>
              <FSButton
                onClick={() => editWeight(row.original)}
                color="primary"
                size="small"
              >
                Edit
              </FSButton>
            </div>
          }
        </div>
      
    },
    { 
      Header: "Actions", 
      Cell: ({ row }) => 
        <FSButton
          onClick={() => removeCounty(row.original)}
          color="red"
          size="small"
        >
          Remove
        </FSButton>
      
    }
  ];

  // Columns for available counties table
  const availableCols = [
    { Header: "County", accessor: "county" },
    { 
      Header: "State", 
      accessor: "state",
      Cell: ({ value }) => getStateName(value)
    },
    { 
      Header: "Actions", 
      Cell: ({ row }) => 
        <FSButton
          onClick={() => addCounty(row.original)}
          color="green"
          size="small"
        >
          Add
        </FSButton>
      
    }
  ];

  // Columns for assigned inspectors table (county-based view)
  const assignedInspectorsCols = [
    { 
      Header: () => 
        <input
          type="checkbox"
          checked={selectAllInspectors}
          onChange={(e) => handleSelectAllInspectors(e.target.checked)}
          className="tw-w-4 tw-h-4 tw-text-blue-600 tw-bg-gray-100 tw-border-gray-300 tw-rounded focus:tw-ring-blue-500"
        />
      ,
      accessor: "id",
      Cell: ({ row }) => 
        <input
          type="checkbox"
          checked={selectedInspectors.has(row.original.id)}
          onChange={(e) => handleSelectInspector(row.original.id, e.target.checked)}
          className="tw-w-4 tw-h-4 tw-text-blue-600 tw-bg-gray-100 tw-border-gray-300 tw-rounded focus:tw-ring-blue-500"
        />
      ,
      width: 50
    },
    { Header: "Inspector", accessor: "inspector.name" },
    { 
      Header: "Weight", 
      accessor: "weight",
      Cell: ({ row }) => 
        <div className="tw-flex tw-items-center tw-gap-2">
          {editingWeight === row.original.id ? 
            <div className="tw-flex tw-items-center tw-gap-2">
              <input
                type="number"
                min="1"
                value={newWeight}
                onChange={(e) => setNewWeight(parseInt(e.target.value) || 1)}
                className="tw-w-16 tw-px-2 tw-py-1 tw-border tw-rounded"
              />
              <FSButton
                onClick={() => saveInspectorWeight(row.original)}
                color="green"
                size="xsmall"
              >
                Save
              </FSButton>
              <FSButton
                onClick={() => setEditingWeight(null)}
                color="secondary"
                size="xsmall"
              >
                Cancel
              </FSButton>
            </div>
            : 
            <div className="tw-flex tw-items-center tw-gap-2">
              <span>{row.original.weight}</span>
              <FSButton
                onClick={() => {
                  setEditingWeight(row.original.id);
                  setNewWeight(row.original.weight);
                }}
                color="primary"
                size="small"
              >
                Edit
              </FSButton>
            </div>
          }
        </div>
      
    },
    { 
      Header: "Actions", 
      Cell: ({ row }) => 
        <FSButton
          onClick={() => removeInspectorFromCounty(row.original)}
          color="red"
          size="small"
        >
          Remove
        </FSButton>
      
    }
  ];

  // Columns for available inspectors table (county-based view)
  const availableInspectorsCols = [
    { Header: "Inspector", accessor: "name" },
    { 
      Header: "Actions", 
      Cell: ({ row }) => 
        <FSButton
          onClick={() => addInspectorToCounty(row.original)}
          color="green"
          size="small"
        >
          Add
        </FSButton>
      
    }
  ];

  const editWeight = (assignedCounty) => {
    setEditingWeight(assignedCounty.id);
    setNewWeight(assignedCounty.weight);
  };

  const saveWeight = async (assignedCounty) => {
    try {
      const res = await apiProvider.put(`counties/inspector/${selectedInspector}/${assignedCounty.county.id}`, {
        weight: newWeight
      });
      if (!mutationOk(res)) {
        toast.error("Error updating weight");
        return;
      }
      
      setEditingWeight(null);
      
      // Refresh assigned counties
      const response = await apiProvider.get(`counties/inspector/${selectedInspector}`);
      setAssignedCounties(response.data.result || response.data || []);
      
      toast.success("Weight updated successfully");
    } catch (error) {
      toast.error("Error updating weight");
      console.error("Error updating weight:", error);
    }
  };

  const addCounty = async (county) => {
    try {
      const res = await apiProvider.post(`counties/inspector/${selectedInspector}`, {
        countyId: county.id,
        weight: 1
      });
      if (!mutationOk(res)) {
        toast.error("Error adding county");
        return;
      }
      
      // Refresh assigned counties
      const response = await apiProvider.get(`counties/inspector/${selectedInspector}`);
      setAssignedCounties(response.data.result || response.data || []);
      
      toast.success(`${county.county}, ${county.state} added successfully`);
    } catch (error) {
      toast.error("Error adding county");
      console.error("Error adding county:", error);
    }
  };

  const removeCounty = async (assignedCounty) => {
    if (!confirm(`Are you sure you want to remove ${assignedCounty.county.county}, ${assignedCounty.county.state}?`)) {
      return;
    }

    try {
      const res = await apiProvider.delete(`counties/inspector/${selectedInspector}`, assignedCounty.county.id);
      if (!mutationOk(res)) {
        toast.error("Error removing county");
        return;
      }
      
      // Refresh assigned counties
      const response = await apiProvider.get(`counties/inspector/${selectedInspector}`);
      setAssignedCounties(response.data.result || response.data || []);
      
      toast.success(`${assignedCounty.county.county}, ${assignedCounty.county.state} removed successfully`);
    } catch (error) {
      toast.error("Error removing county");
      console.error("Error removing county:", error);
    }
  };

  const addAllAvailableCounties = async () => {
    if (!selectedState) {
      toast.error("Please select a state first");
      return;
    }
    const countiesToAdd = availableCounties;
    if (countiesToAdd.length === 0) {
      toast.error(`No counties available to add in ${getStateName(selectedState)}`);
      return;
    }
    if (!confirm(`Add this inspector to all ${countiesToAdd.length} counties in ${getStateName(selectedState)}?`)) {
      return;
    }

    setBatchLoading(true);
    try {
      const res = await apiProvider.post(`counties/inspector/${selectedInspector}/state/${selectedState}`, {
        weight: 1
      });
      if (!mutationOk(res)) {
        toast.error("Error adding counties");
        return;
      }
      const response = await apiProvider.get(`counties/inspector/${selectedInspector}`);
      setAssignedCounties(response.data.result || response.data || []);
      toast.success(`Added all available counties in ${getStateName(selectedState)}`);
    } catch (error) {
      toast.error("Error adding counties");
      console.error("Error adding counties:", error);
    } finally {
      setBatchLoading(false);
    }
  };

  const clearStateFromInspector = async () => {
    if (!selectedState) {
      toast.error("Please select a state first");
      return;
    }
    const stateAssigned = assignedCounties.filter(ac => ac.county.state === selectedState);
    if (stateAssigned.length === 0) {
      toast.error(`No counties assigned to this inspector in ${getStateName(selectedState)}`);
      return;
    }
    if (!confirm(`Remove all ${stateAssigned.length} counties in ${getStateName(selectedState)} from this inspector?`)) {
      return;
    }

    setBatchLoading(true);
    try {
      const res = await apiProvider.delete(`counties/inspector/${selectedInspector}/state`, selectedState);
      if (!mutationOk(res)) {
        toast.error("Error clearing state");
        return;
      }
      const response = await apiProvider.get(`counties/inspector/${selectedInspector}`);
      setAssignedCounties(response.data.result || response.data || []);
      toast.success(`Removed all counties in ${getStateName(selectedState)} from this inspector`);
    } catch (error) {
      toast.error("Error clearing state");
      console.error("Error clearing state:", error);
    } finally {
      setBatchLoading(false);
    }
  };

  const deleteAllAssignedCounties = async () => {
    if (assignedCounties.length === 0) {
      toast.error("No counties assigned to delete");
      return;
    }

    if (!confirm(`Are you sure you want to remove ALL ${assignedCounties.length} counties from this inspector? This action cannot be undone.`)) {
      return;
    }

    setBatchLoading(true);
    try {
      const res = await apiProvider.delete("counties/inspector", selectedInspector);
      if (!mutationOk(res)) {
        toast.error("Error removing counties");
        return;
      }
      const response = await apiProvider.get(`counties/inspector/${selectedInspector}`);
      setAssignedCounties(response.data.result || response.data || []);
      toast.success(`Removed all ${assignedCounties.length} counties from this inspector`);
    } catch (error) {
      toast.error("Error removing counties");
      console.error("Error removing counties:", error);
    } finally {
      setBatchLoading(false);
    }
  };

  const deleteSelectedCounties = async () => {
    if (selectedCounties.size === 0) {
      toast.error("Please select counties to delete");
      return;
    }

    if (!confirm(`Are you sure you want to remove ${selectedCounties.size} selected counties from this inspector?`)) {
      return;
    }

    setBatchLoading(true);
    try {
      const countiesToDelete = assignedCounties.filter(ac => selectedCounties.has(ac.id));
      const countyIds = countiesToDelete.map(ac => ac.county.id);

      const res = await apiProvider.delete(`counties/inspector/${selectedInspector}`, "counties", { countyIds });
      if (!mutationOk(res)) {
        toast.error("Error removing selected counties");
        return;
      }

      // Refresh assigned counties
      const response = await apiProvider.get(`counties/inspector/${selectedInspector}`);
      setAssignedCounties(response.data.result || response.data || []);

      // Clear selections
      setSelectedCounties(new Set());
      setSelectAll(false);

      toast.success(`Removed ${countyIds.length} counties from this inspector`);
    } catch (error) {
      toast.error("Error removing selected counties");
      console.error("Error removing selected counties:", error);
    } finally {
      setBatchLoading(false);
    }
  };

  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      const allIds = new Set(assignedCounties.map(ac => ac.id));
      setSelectedCounties(allIds);
    } else {
      setSelectedCounties(new Set());
    }
  };

  const handleSelectCounty = (countyId, checked) => {
    const newSelected = new Set(selectedCounties);
    if (checked) {
      newSelected.add(countyId);
    } else {
      newSelected.delete(countyId);
    }
    setSelectedCounties(newSelected);
    setSelectAll(newSelected.size === assignedCounties.length);
  };

  // County-based view functions
  const addInspectorToCounty = async (inspector) => {
    try {
      const res = await apiProvider.post(`counties/inspector/${inspector.id}`, {
        countyId: selectedCounty.id,
        weight: 1
      });
      if (!mutationOk(res)) {
        toast.error("Error assigning inspector");
        return;
      }
      
      // Refresh the county view data
      refreshCountyView();
      
      toast.success(`${inspector.name} assigned to ${selectedCounty.county}, ${selectedCounty.state}`);
    } catch (error) {
      toast.error("Error assigning inspector");
      console.error("Error assigning inspector:", error);
    }
  };

  const removeInspectorFromCounty = async (assignedInspector) => {
    if (!confirm(`Are you sure you want to remove ${assignedInspector.inspector.name} from ${selectedCounty.county}, ${selectedCounty.state}?`)) {
      return;
    }

    try {
      const res = await apiProvider.delete(`counties/inspector/${assignedInspector.inspector.id}`, selectedCounty.id);
      if (!mutationOk(res)) {
        toast.error("Error removing inspector");
        return;
      }
      
      // Refresh the county view data
      refreshCountyView();
      
      toast.success(`${assignedInspector.inspector.name} removed from ${selectedCounty.county}, ${selectedCounty.state}`);
    } catch (error) {
      toast.error("Error removing inspector");
      console.error("Error removing inspector:", error);
    }
  };

  const saveInspectorWeight = async (assignedInspector) => {
    try {
      const res = await apiProvider.put(`counties/inspector/${assignedInspector.inspector.id}/${selectedCounty.id}`, {
        weight: newWeight
      });
      if (!mutationOk(res)) {
        toast.error("Error updating weight");
        return;
      }
      
      setEditingWeight(null);
      
      // Refresh the county view data
      refreshCountyView();
      
      toast.success("Weight updated successfully");
    } catch (error) {
      toast.error("Error updating weight");
      console.error("Error updating weight:", error);
    }
  };

  const refreshCountyView = async () => {
    if (!selectedCounty) {return;}
    setLoading(true);
    await loadCountyInspectors(selectedCounty.id);
  };

  const handleSelectAllInspectors = (checked) => {
    setSelectAllInspectors(checked);
    if (checked) {
      const allIds = new Set(assignedInspectors.map(ai => ai.id));
      setSelectedInspectors(allIds);
    } else {
      setSelectedInspectors(new Set());
    }
  };

  const handleSelectInspector = (inspectorId, checked) => {
    const newSelected = new Set(selectedInspectors);
    if (checked) {
      newSelected.add(inspectorId);
    } else {
      newSelected.delete(inspectorId);
    }
    setSelectedInspectors(newSelected);
    setSelectAllInspectors(newSelected.size === assignedInspectors.length);
  };

  return (
    <>
      <h1 className="tw-text-2xl tw-font-bold tw-mb-6">Inspector County Mapping</h1>

      {/* Tab Navigation */}
      <div className="tw-mb-6 tw-border-b tw-border-gray-200">
        <nav className="tw-flex tw-space-x-8">
          {canEdit &&
          <button
            onClick={() => setActiveTab("inspector")}
            className={`tw-py-2 tw-px-1 tw-border-b-2 tw-font-medium tw-text-sm ${
              activeTab === "inspector"
                ? "tw-border-blue-500 tw-text-blue-600"
                : "tw-border-transparent tw-text-gray-500 hover:tw-text-gray-700 hover:tw-border-gray-300"
            }`}
          >
            By Inspector
          </button>
          }
          {canEdit &&
          <button
            onClick={() => setActiveTab("county")}
            className={`tw-py-2 tw-px-1 tw-border-b-2 tw-font-medium tw-text-sm ${
              activeTab === "county"
                ? "tw-border-blue-500 tw-text-blue-600"
                : "tw-border-transparent tw-text-gray-500 hover:tw-text-gray-700 hover:tw-border-gray-300"
            }`}
          >
            By County
          </button>
          }
          {canViewReport &&
          <button
            onClick={() => setActiveTab("report")}
            className={`tw-py-2 tw-px-1 tw-border-b-2 tw-font-medium tw-text-sm ${
              activeTab === "report"
                ? "tw-border-blue-500 tw-text-blue-600"
                : "tw-border-transparent tw-text-gray-500 hover:tw-text-gray-700 hover:tw-border-gray-300"
            }`}
          >
            Report
          </button>
          }
        </nav>
      </div>

      {/* Inspector-based View */}
      {canEdit && activeTab === "inspector" && 
        <>
          {/* Inspector Selector */}
          <div className="tw-mb-8 tw-flex tw-gap-4 tw-items-baseline">
            <label className="tw-text-lg tw-font-medium">Inspector:</label>
            {inspectors && Array.isArray(inspectors) && 
              <select 
                className="tw-flex-grow tw-max-w-md tw-rounded-lg tw-border tw-border-gray-300 tw-px-3 tw-py-2"
                value={selectedInspector || ""}
                onChange={(e) => setSelectedInspector(e.target.value || null)}
              >
                <option value="">Choose inspector...</option>
                {inspectors.map((inspector) => 
                  <option key={inspector.id} value={inspector.id}>
                    {inspector.name}
                  </option>
                )}
              </select>
            }
          </div>

          {selectedInspector && 
            <>
              {/* Assigned Counties Section */}
              <div className="tw-mb-10">
                <div className="tw-flex tw-justify-between tw-items-center tw-mb-4">
                  <h2 className="tw-text-xl tw-font-bold">
                    Assigned Counties {loading && <span className="tw-text-sm tw-text-gray-500">(Loading...)</span>}
                  </h2>
                  {assignedCounties.length > 0 && 
                    <div className="tw-flex tw-gap-2">
                      <FSButton
                        onClick={selectedCounties.size > 0 ? deleteSelectedCounties : deleteAllAssignedCounties}
                        disabled={batchLoading}
                        submitting={batchLoading}
                        color={selectedCounties.size > 0 ? "red" : "red"}
                        size="small"
                      >
                        {selectedCounties.size > 0 
                          ? `Delete Selected (${selectedCounties.size})`
                          : `Delete All (${assignedCounties.length})`
                        }
                      </FSButton>
                    </div>
                  }
                </div>
                {assignedCounties.length > 0 ? 
                  <Table data={assignedCounties} columns={assignedCols} />
                  : 
                  <div className="tw-text-gray-500 tw-italic tw-p-4 tw-border tw-rounded">
                    No counties assigned to this inspector
                  </div>
                }
              </div>

              {/* Available Counties Section */}
              <div>
                <h2 className="tw-text-xl tw-font-bold tw-mb-4">Available Counties</h2>
                
                {/* Filters */}
                <div className="tw-mb-4 tw-flex tw-gap-4 tw-items-center tw-flex-wrap">
                  <div className="tw-flex tw-items-center tw-gap-2">
                    <label className="tw-font-medium">State:</label>
                    <select
                      value={selectedState}
                      onChange={(e) => setSelectedState(e.target.value)}
                      className="tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded"
                    >
                      <option value="">Select a state to view counties...</option>
                      {Array.isArray(states) && states.map(state => 
                        <option key={state} value={state}>{getStateName(state)}</option>
                      )}
                    </select>
                  </div>
                  {selectedState && 
                    <div className="tw-flex tw-items-center tw-gap-2">
                      <label className="tw-font-medium">Search Counties:</label>
                      <input
                        type="text"
                        placeholder="County name..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded"
                      />
                    </div>
                  }
                  {selectedState && availableCounties.length > 0 && 
                    <FSButton
                      onClick={addAllAvailableCounties}
                      disabled={batchLoading}
                      submitting={batchLoading}
                      color="primary"
                      size="regular"
                    >
                      Add all {availableCounties.length} counties in {getStateName(selectedState)}
                    </FSButton>
                  }
                  {selectedState && stateAssignedCount > 0 && 
                    <FSButton
                      onClick={clearStateFromInspector}
                      disabled={batchLoading}
                      submitting={batchLoading}
                      color="red"
                      size="regular"
                    >
                      Clear all in {getStateName(selectedState)} ({stateAssignedCount})
                    </FSButton>
                  }
                </div>

                {!selectedState ? 
                  <div className="tw-text-gray-500 tw-italic tw-p-4 tw-border tw-rounded">
                    Please select a state to view available counties
                  </div>
                  : loadingCounties ? 
                    <div className="tw-text-gray-500 tw-italic tw-p-4 tw-border tw-rounded">
                    Loading counties for {selectedState}...
                    </div>
                    : filteredAvailableCounties.length > 0 ? 
                      <Table data={filteredAvailableCounties} columns={availableCols} />
                      : stateCounties.length > 0 ? 
                        <div className="tw-text-gray-500 tw-italic tw-p-4 tw-border tw-rounded">
                          {searchTerm ? `No counties in ${selectedState} match "${searchTerm}"` : `All counties in ${selectedState} are assigned to this inspector`}
                        </div>
                        :
                        <div className="tw-text-gray-500 tw-italic tw-p-4 tw-border tw-rounded">
                    No counties found for {selectedState}
                        </div>
                }
              </div>
            </>
          }

          {!selectedInspector && 
            <div className="tw-text-gray-500 tw-italic tw-text-center tw-p-8">
              Select an inspector to manage their county assignments
            </div>
          }
        </>
      }

      {/* County-based View */}
      {canEdit && activeTab === "county" && 
        <>
          {/* State and County Selectors */}
          <div className="tw-mb-8 tw-space-y-4">
            {/* State Selector */}
            <div className="tw-flex tw-gap-4 tw-items-baseline">
              <label className="tw-text-lg tw-font-medium tw-w-20">State:</label>
              <select 
                className="tw-flex-grow tw-max-w-md tw-rounded-lg tw-border tw-border-gray-300 tw-px-3 tw-py-2"
                value={selectedCountyState}
                onChange={(e) => setSelectedCountyState(e.target.value)}
              >
                <option value="">Choose state...</option>
                {Array.isArray(states) && states.map(state => 
                  <option key={state} value={state}>{getStateName(state)}</option>
                )}
              </select>
            </div>

            {/* County Selector */}
            {selectedCountyState && 
              <div className="tw-flex tw-gap-4 tw-items-baseline">
                <label className="tw-text-lg tw-font-medium tw-w-20">County:</label>
                {loadingCounties ? 
                  <div className="tw-flex-grow tw-max-w-md tw-px-3 tw-py-2 tw-text-gray-500">
                    Loading counties for {getStateName(selectedCountyState)}...
                  </div>
                  : 
                  <select 
                    className="tw-flex-grow tw-max-w-md tw-rounded-lg tw-border tw-border-gray-300 tw-px-3 tw-py-2"
                    value={selectedCounty ? `${selectedCounty.id}` : ""}
                    onChange={(e) => {
                      const countyId = e.target.value;
                      if (countyId) {
                        const county = countyStateCounties.find(c => c.id === parseInt(countyId));
                        setSelectedCounty(county);
                      } else {
                        setSelectedCounty(null);
                      }
                    }}
                  >
                    <option value="">Choose county...</option>
                    {countyStateCounties.map((county) => 
                      <option key={county.id} value={county.id}>
                        {county.county}
                      </option>
                    )}
                  </select>
                }
              </div>
            }
          </div>

          {selectedCounty && 
            <>
              {/* Assigned Inspectors Section */}
              <div className="tw-mb-10">
                <div className="tw-flex tw-justify-between tw-items-center tw-mb-4">
                  <h2 className="tw-text-xl tw-font-bold">
                    Assigned Inspectors {loading && <span className="tw-text-sm tw-text-gray-500">(Loading...)</span>}
                  </h2>
                </div>
                {assignedInspectors.length > 0 ? 
                  <Table data={assignedInspectors} columns={assignedInspectorsCols} />
                  : 
                  <div className="tw-text-gray-500 tw-italic tw-p-4 tw-border tw-rounded">
                    No inspectors assigned to this county
                  </div>
                }
              </div>

              {/* Available Inspectors Section */}
              <div>
                <h2 className="tw-text-xl tw-font-bold tw-mb-4">Available Inspectors</h2>
                {availableInspectors.length > 0 ? 
                  <Table data={availableInspectors} columns={availableInspectorsCols} />
                  : 
                  <div className="tw-text-gray-500 tw-italic tw-p-4 tw-border tw-rounded">
                    All inspectors are assigned to this county
                  </div>
                }
              </div>
            </>
          }

          {!selectedCounty && 
            <div className="tw-text-gray-500 tw-italic tw-text-center tw-p-8">
              {!selectedCountyState 
                ? "Select a state and county to manage inspector assignments"
                : "Select a county to manage inspector assignments"
              }
            </div>
          }
        </>
      }

      {/* Report View */}
      {canViewReport && activeTab === "report" && <ReportView rows={reportRows} loading={reportLoading} expandedStates={expandedStates} setExpandedStates={setExpandedStates} />}
    </>
  );
}

// Group report rows by state then county, computing each inspector's share of
// the total weight assigned to that county.
function groupReport(rows) {
  const byState = new Map();
  for (const r of rows) {
    if (!byState.has(r.state)) {byState.set(r.state, new Map());}
    const counties = byState.get(r.state);
    if (!counties.has(r.countyId)) {
      counties.set(r.countyId, { countyId: r.countyId, county: r.county, totalWeight: 0, inspectors: [] });
    }
    const c = counties.get(r.countyId);
    c.totalWeight += r.weight;
    c.inspectors.push({ inspectorId: r.inspectorId, inspector: r.inspector, weight: r.weight });
  }
  const result = [];
  for (const [state, counties] of byState) {
    const countyList = [...counties.values()].sort((a, b) => a.county.localeCompare(b.county));
    for (const c of countyList) {
      c.inspectors.sort((a, b) => a.inspector.localeCompare(b.inspector));
    }
    result.push({ state, counties: countyList });
  }
  result.sort((a, b) => a.state.localeCompare(b.state));
  return result;
}

function ReportView({ rows, loading, expandedStates, setExpandedStates }) {
  if (loading) {
    return <div className="tw-text-gray-500 tw-italic tw-p-4">Loading report...</div>;
  }
  if (!rows || rows.length === 0) {
    return <div className="tw-text-gray-500 tw-italic tw-p-4 tw-border tw-rounded">No inspector county assignments found</div>;
  }

  const grouped = groupReport(rows);

  const toggle = (state) => {
    const next = new Set(expandedStates);
    if (next.has(state)) {next.delete(state);} else {next.add(state);}
    setExpandedStates(next);
  };

  return (
    <div className="tw-space-y-2">
      {grouped.map(({ state, counties }) => {
        const isOpen = expandedStates.has(state);
        const inspectorCount = counties.reduce((n, c) => n + c.inspectors.length, 0);
        return (
          <div key={state} className="tw-border tw-rounded">
            <button
              onClick={() => toggle(state)}
              className="tw-w-full tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3 tw-bg-gray-50 hover:tw-bg-gray-100 tw-text-left"
            >
              <span className="tw-font-semibold tw-text-lg">
                {isOpen ? "▼" : "▶"} {getStateName(state)}
              </span>
              <span className="tw-text-sm tw-text-gray-500">
                {counties.length} count{counties.length !== 1 ? "ies" : "y"} · {inspectorCount} assignment{inspectorCount !== 1 ? "s" : ""}
              </span>
            </button>
            {isOpen && 
              <div className="tw-overflow-x-auto">
                <table className="tw-min-w-full tw-text-sm">
                  <thead className="tw-bg-gray-100 tw-text-gray-600 tw-uppercase tw-text-xs">
                    <tr>
                      <th className="tw-px-4 tw-py-2 tw-text-left">County</th>
                      <th className="tw-px-4 tw-py-2 tw-text-left">Inspector</th>
                      <th className="tw-px-4 tw-py-2 tw-text-right">Weight</th>
                      <th className="tw-px-4 tw-py-2 tw-text-right">Percentage</th>
                    </tr>
                  </thead>
                  <tbody>
                    {counties.map((c) => 
                      c.inspectors.map((insp, i) => 
                        <tr key={`${c.countyId}-${insp.inspectorId}`} className={i === 0 ? "" : "tw-border-t tw-border-gray-100"}>
                          {i === 0 && 
                            <td className="tw-px-4 tw-py-2 tw-align-top tw-font-medium" rowSpan={c.inspectors.length}>
                              {c.county}
                            </td>
                          }
                          <td className="tw-px-4 tw-py-2">{insp.inspector}</td>
                          <td className="tw-px-4 tw-py-2 tw-text-right">{insp.weight}</td>
                          <td className="tw-px-4 tw-py-2 tw-text-right">
                            {c.totalWeight > 0 ? (insp.weight / c.totalWeight * 100).toFixed(1) + "%" : "—"}
                          </td>
                        </tr>
                      )
                    )}
                  </tbody>
                </table>
              </div>
            }
          </div>
        );
      })}
    </div>
  );
}
 